/**
 * MFE (Micro-Frontend) Application Shell
 *
 * Provides singleton initialization and utilities for the MicroFrontendApplication.
 * This module manages the global MFE app instance and frontend registrations.
 *
 * Usage:
 * - Call initMfeApp() once at application bootstrap (in main.tsx)
 * - Use isMfeAvailable() to check if a frontend is ready before rendering touchpoints
 */

import { getAppBaseMfeUrl, getCpqBaseUrl, getCpqMfeUrl } from "./vite-env";

// Define minimal types to avoid bundling during SSG
type TouchpointDeclaration = string | { name: string; properties?: Record<string, unknown> };

interface MicroFrontendConfig {
    originUrl: string;
    useTouchpoints: TouchpointDeclaration[];
}

interface MicroFrontendApplication {
    registerFrontend: (name: string, config: MicroFrontendConfig) => MicroFrontendApplication;
    start: (options?: MicroFrontendApplicationOptions) => Promise<MicroFrontendApplication>;
}

type MicroFrontendApplicationOptions = { lazy?: boolean; beforeAll?: () => void };

type Spritemap = Record<string, string> | undefined;

/** Global MFE application singleton instance */
let mfeAppInstance: MicroFrontendApplication | null = null;

/** Tracks initialization state */
let isInitializing = false;
let isInitialized = false;

/**
 * Convert absolute localhost URLs to relative paths so they go through the
 * Vite dev-server proxy (avoids CORS issues during development).
 * Production URLs (non-localhost) are left unchanged.
 */
const toProxyUrl = (url: string): string => {
    try {
        const parsed = new URL(url);
        if (parsed.hostname === "localhost" || parsed.hostname === "127.0.0.1") {
            return parsed.pathname;
        }
    } catch {
        // Already a relative URL — keep as-is
    }
    return url;
};

/**
 * Configuration for MFE remote URLs
 */
const getMfeConfig = () => {
    // Use Vite's environment variable (available at build and runtime)
    const cpqMfeUrl = getCpqMfeUrl();
    const appBaseMfeUrl = getAppBaseMfeUrl();

    return {
        cpqPlayground: {
            enabled: !!cpqMfeUrl,
            originUrl: cpqMfeUrl ? toProxyUrl(cpqMfeUrl) : "",
        },
        appBase: {
            enabled: !!appBaseMfeUrl,
            originUrl: appBaseMfeUrl ? toProxyUrl(appBaseMfeUrl) : "",
        },
    };
};

/**
 * Initialize the global MFE application (singleton pattern).
 *
 * Should be called once at application bootstrap, before React renders.
 * Safe to call multiple times - subsequent calls are no-ops.
 *
 * Features:
 * - Lazy loading (MFE modules load on first touchpoint render)
 * - Graceful degradation (errors logged but don't crash app)
 * - Environment-driven (only enabled when VITE_CPQ_MFE_URL is set)
 *
 * @example
 * ```typescript
 * // In main.tsx, before React render:
 * if (typeof window !== "undefined") {
 *     initMfeApp().catch(err => {
 *         console.error("[MFE] Bootstrap failed:", err);
 *     });
 * }
 * ```
 */
export const initMfeApp = async (): Promise<void> => {
    // Skip if already initialized or initializing
    if (isInitialized || isInitializing) {
        return;
    }

    // Skip during SSR/SSG builds
    if (typeof window === "undefined") {
        console.warn("[MFE] Skipping initialization during SSR/SSG build");
        return;
    }

    isInitializing = true;

    try {
        const config = getMfeConfig();

        // Only initialize if at least one MFE is configured
        if (!config.cpqPlayground.enabled && !config.appBase.enabled) {
            console.info("[MFE] No MFE URLs configured, skipping initialization");
            isInitialized = true; // Mark as initialized to prevent retries
            return;
        }

        console.info("[MFE] Initializing MicroFrontendApplication...");

        // Dynamic import to avoid bundling in SSG
        const { MicroFrontendApplication } = await import("@qs/app-shell");

        const app = new MicroFrontendApplication();
        // Register CPQ Playground frontend
        if (config.cpqPlayground.enabled) {
            console.info("[MFE] Registering cpq-playground frontend:", config.cpqPlayground.originUrl);
            app.registerFrontend("cpq-playground", {
                originUrl: config.cpqPlayground.originUrl,
                useTouchpoints: ["service-definition-playground", "service-definition-preview"],
            });

            // Register cpq-cds (same MFE, used as inherited frontend by app-base onboarding)
            app.registerFrontend("cpq-cds", {
                originUrl: config.cpqPlayground.originUrl,
                useTouchpoints: ["service-definition-playground", "service-definition-preview"],
            });
        }

        // Register App-Base frontend (onboarding touchpoint)
        if (config.appBase.enabled) {
            console.info("[MFE] Registering app-base frontend:", config.appBase.originUrl);
            app.registerFrontend("app-base", {
                originUrl: config.appBase.originUrl,
                useTouchpoints: [
                    {
                        name: "onboarding",
                        properties: {
                            frontend: "cpq-cds",
                            touchpoint: "service-definition-playground",
                            edition: "starter",
                        },
                    },
                ],
            });
        }
        // Initialize webbloqs global configuration for CSS loading
        const cpqBaseUrl = getCpqBaseUrl();
        const publicPath = cpqBaseUrl ? `${cpqBaseUrl}/ui/assets` : undefined;
        const spritemapJson = publicPath
            ? ((await fetch(`${publicPath}/icons-spritemap.json`)
                  .then(res => res.json())
                  .catch(() => undefined)) as Spritemap)
            : undefined;

        const { initGlobals } = await import("@webbloqs/elements/lib/global");
        const { setLanguage } = await import("@webbloqs/react");

        // Start with lazy loading (modules load on first touchpoint render)
        await app.start({
            lazy: true,
            beforeAll: () => {
                initGlobals({
                    spritemap: { icons: spritemapJson },
                    enableCustomMediaQueries: true,
                    publicPath: publicPath ?? "",
                });
                setLanguage("de");
            },
        });

        mfeAppInstance = app;
        isInitialized = true;
        console.info("[MFE] Application initialized successfully");
    } catch (error) {
        console.warn("[MFE] Failed to initialize:", error);
        // Don't throw - allow app to continue without MFE features
    } finally {
        isInitializing = false;
    }
};

/** Track health check state per frontend */
const healthCheckState: Record<string, { passed: boolean; inProgress: boolean }> = {};

/**
 * Check if an MFE server is reachable by attempting to fetch its mfe.js file.
 * This prevents console errors when the server is not running.
 *
 * @param url - The MFE origin URL to check
 * @param key - Cache key for the health check state
 * @returns true if the MFE server is reachable
 */
const checkMfeHealth = async (url: string, key: string): Promise<boolean> => {
    const state = healthCheckState[key] ?? { passed: false, inProgress: false };
    healthCheckState[key] = state;

    if (state.passed) {
        return true;
    }

    if (state.inProgress) {
        return false;
    }

    state.inProgress = true;

    try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 2000);

        const response = await fetch(url, {
            method: "HEAD",
            signal: controller.signal,
            cache: "no-cache",
        });

        clearTimeout(timeoutId);

        if (response.ok) {
            state.passed = true;
            return true;
        }
    } catch (err) {
        console.info(`[MFE] Health check failed for ${key}:`, err instanceof Error ? err.message : err);
    } finally {
        state.inProgress = false;
    }

    return false;
};

/**
 * Check if a specific frontend is available.
 *
 * Use this before rendering touchpoints to determine if the MFE is ready.
 * Returns false if:
 * - MFE app not initialized
 * - Frontend not registered
 * - Environment variable not set
 * - MFE server not reachable (health check failed)
 *
 * @param frontend - Frontend identifier (e.g., "cpq-playground")
 * @returns true if frontend is ready to use
 *
 * @example
 * ```typescript
 * if (!isMfeAvailable("cpq-playground")) {
 *     return null; // Don't render touchpoint
 * }
 * ```
 */
export const isMfeAvailable = (frontend: string): boolean => {
    if (!isInitialized || !mfeAppInstance) {
        return false;
    }

    const config = getMfeConfig();

    if (frontend === "cpq-playground") {
        return config.cpqPlayground.enabled && (healthCheckState["cpq-playground"]?.passed ?? false);
    }

    if (frontend === "app-base") {
        return config.appBase.enabled && (healthCheckState["app-base"]?.passed ?? false);
    }

    return false;
};

/**
 * Asynchronously check if a frontend is available (includes health check).
 * Use this in components to verify the MFE server is reachable before rendering.
 *
 * @param frontend - Frontend identifier (e.g., "cpq-playground")
 * @returns Promise<boolean> - true if frontend is ready and server is reachable
 */
export const isMfeAvailableAsync = async (frontend: string): Promise<boolean> => {
    if (!isInitialized || !mfeAppInstance) {
        return false;
    }

    const config = getMfeConfig();

    if (frontend === "cpq-playground") {
        if (!config.cpqPlayground.enabled) {
            return false;
        }
        return await checkMfeHealth(config.cpqPlayground.originUrl, "cpq-playground");
    }

    if (frontend === "app-base") {
        if (!config.appBase.enabled) {
            return false;
        }
        return await checkMfeHealth(config.appBase.originUrl, "app-base");
    }

    return false;
};

/**
 * Get initialization status (useful for debugging)
 */
export const getMfeStatus = () => ({
    isInitializing,
    isInitialized,
    hasInstance: !!mfeAppInstance,
    config: getMfeConfig(),
});
